import { CartModifier, ICartModifier } from './CartModifier';
import {
    resolveModifierSelectionRules,
    validateModifierSelection,
    type ModifierSelectionRules,
} from '../menus/modifierSelectionRules';

/**
 * A modifier group as SNAPSHOTTED onto a cart line.
 *
 * The rule fields are a snapshot of what this line was allowed to contain,
 * already overlaid with the item's per-group `ModifierGroupReference` — the
 * menu can change under a saved cart, and re-resolving against the live menu
 * would silently re-rule a line the guest already built.
 *
 * ## Why the extra fields exist
 *
 * This carried only `selectionRequirement` / `minSelection` / `maxSelection`,
 * which cannot express duplicates. Re-opening a saved line therefore had to
 * assume `allowDuplicateSelection: false` (see `MenuPageClient`), so a guest
 * editing a line in a group that allows the same option more than once lost
 * the quantity steppers and could not adjust what they had already chosen.
 * The unique and per-modifier caps were absent for the same reason.
 *
 * Every field is optional and every reader goes through
 * [selectionRules], so a cart document written before these existed resolves
 * exactly as it always did.
 */
export interface ICartModifierGroup {
    readonly modifierGroupId: number;
    readonly name: string;
    readonly modifiers: ICartModifier[];
    readonly selectionRequirement: 'Optional' | 'Required';
    readonly minSelection: number;
    readonly maxSelection?: number;
    readonly allowMultipleSelection?: boolean;
    readonly allowDuplicateSelection?: boolean;
    readonly maxDuplicatesPerModifier?: number;
    readonly minUniqueSelection?: number;
    readonly maxUniqueSelection?: number;
}

export interface ICartModifierGroupData {
    modifierGroupId: number;
    name: string;
    modifiers?: CartModifier[];
    selectionRequirement?: 'Optional' | 'Required';
    minSelection?: number;
    maxSelection?: number;
    allowMultipleSelection?: boolean;
    allowDuplicateSelection?: boolean;
    maxDuplicatesPerModifier?: number;
    minUniqueSelection?: number;
    maxUniqueSelection?: number;
}

/**
 * Project reconciled [ModifierSelectionRules] into the fields a cart line
 * snapshots. Every surface that writes a cart line goes through this, so the
 * web modal, the assistant and anything added later cannot snapshot different
 * subsets of the same rules.
 *
 * `null` becomes `undefined` because "no cap" is an ABSENT key on the
 * document — Firestore rejects an undefined value and stores null as a real
 * null, which `fromJSON` would then have to special-case.
 */
export function cartSelectionRuleSnapshot(
    rules: ModifierSelectionRules,
): Pick<
    ICartModifierGroupData,
    | 'selectionRequirement'
    | 'minSelection'
    | 'maxSelection'
    | 'allowMultipleSelection'
    | 'allowDuplicateSelection'
    | 'maxDuplicatesPerModifier'
    | 'minUniqueSelection'
    | 'maxUniqueSelection'
> {
    return {
        selectionRequirement: rules.isRequired ? 'Required' : 'Optional',
        minSelection: rules.minSelection,
        ...(rules.maxSelection !== null ? { maxSelection: rules.maxSelection } : {}),
        allowMultipleSelection: rules.allowMultiple,
        allowDuplicateSelection: rules.allowDuplicate,
        // Only meaningful when duplicates are allowed — with them off the cap
        // of one is implied, and writing it to every cart document is noise.
        ...(rules.allowDuplicate && rules.maxPerModifier !== null
            ? { maxDuplicatesPerModifier: rules.maxPerModifier }
            : {}),
        ...(rules.minUnique !== null ? { minUniqueSelection: rules.minUnique } : {}),
        ...(rules.maxUnique !== null ? { maxUniqueSelection: rules.maxUnique } : {})
    };
}

export class CartModifierGroup implements ICartModifierGroup {
    readonly modifierGroupId: number;
    readonly name: string;
    readonly modifiers: CartModifier[];
    readonly selectionRequirement: 'Optional' | 'Required';
    readonly minSelection: number;
    readonly maxSelection?: number;
    readonly allowMultipleSelection?: boolean;
    readonly allowDuplicateSelection?: boolean;
    readonly maxDuplicatesPerModifier?: number;
    readonly minUniqueSelection?: number;
    readonly maxUniqueSelection?: number;

    constructor(data: ICartModifierGroupData) {
        this.modifierGroupId = data.modifierGroupId;
        this.name = data.name;
        this.modifiers = data.modifiers ?? [];
        this.selectionRequirement = data.selectionRequirement ?? 'Optional';
        this.minSelection = data.minSelection ?? 0;
        this.maxSelection = data.maxSelection;
        this.allowMultipleSelection = data.allowMultipleSelection;
        this.allowDuplicateSelection = data.allowDuplicateSelection;
        this.maxDuplicatesPerModifier = data.maxDuplicatesPerModifier;
        this.minUniqueSelection = data.minUniqueSelection;
        this.maxUniqueSelection = data.maxUniqueSelection;
    }

    /**
     * The reconciled rules for this line — the same answer every other surface
     * gets, because the class satisfies `ModifierSelectionRuleSource` and the
     * shared resolver does the reconciling.
     *
     * Idempotent over a snapshot: resolving already-resolved values returns
     * them unchanged, so a round trip through the cart cannot tighten or
     * loosen a line.
     */
    get selectionRules(): ModifierSelectionRules {
        return resolveModifierSelectionRules(this);
    }

    get totalPriceDelta(): number {
        return this.modifiers.reduce((total, modifier) => total + modifier.totalPriceDelta, 0);
    }

    get totalModifierCount(): number {
        return this.modifiers.reduce((total, modifier) => total + modifier.quantity, 0);
    }

    /** One entry per INSTANCE — `quantity: 3` is three selections, not one. */
    private selectedInstanceIds(): number[] {
        const ids: number[] = [];
        for (const modifier of this.modifiers) {
            const quantity = modifier.quantity > 0 ? modifier.quantity : 1;
            for (let i = 0; i < quantity; i += 1) ids.push(modifier.modifierId);
        }
        return ids;
    }

    /**
     * Whether this line satisfies its own snapshotted rules.
     *
     * Was `selectionRequirement === 'Required' && totalCount < this.minSelection`.
     * Management writes `minSelection: 0` on every group it saves, so that read
     * as `totalCount < 0` — never true — and a REQUIRED group validated while
     * empty. It also checked only an explicit `maxSelection`, so a
     * single-select group's implicit cap of one went unenforced, and it never
     * checked duplicate or unique limits at all.
     */
    isValidSelection(): boolean {
        return validateModifierSelection(this.selectedInstanceIds(), this.selectionRules) === null;
    }

    /** The first rule this line breaks, or `null` — the reason behind [isValidSelection]. */
    selectionError(): string | null {
        return (
            validateModifierSelection(this.selectedInstanceIds(), this.selectionRules)?.message ??
            null
        );
    }

    toJSON(): ICartModifierGroup {
        // Optional keys are omitted rather than written as undefined: Firestore
        // rejects an undefined value, and an absent key is exactly what a
        // pre-existing document looks like.
        return {
            modifierGroupId: this.modifierGroupId,
            name: this.name,
            modifiers: this.modifiers.map(modifier => modifier.toJSON()),
            selectionRequirement: this.selectionRequirement,
            minSelection: this.minSelection,
            // `maxSelection: undefined` was emitted unconditionally. Cart items
            // are stored as a whole ARRAY on the cart document, so the array is
            // replaced wholesale on every write and an omitted key cannot leave
            // a stale value behind — there is nothing to gain by writing the
            // undefined, and Firestore rejects it.
            ...(this.maxSelection !== undefined ? { maxSelection: this.maxSelection } : {}),
            ...(this.allowMultipleSelection !== undefined
                ? { allowMultipleSelection: this.allowMultipleSelection }
                : {}),
            ...(this.allowDuplicateSelection !== undefined
                ? { allowDuplicateSelection: this.allowDuplicateSelection }
                : {}),
            ...(this.maxDuplicatesPerModifier !== undefined
                ? { maxDuplicatesPerModifier: this.maxDuplicatesPerModifier }
                : {}),
            ...(this.minUniqueSelection !== undefined
                ? { minUniqueSelection: this.minUniqueSelection }
                : {}),
            ...(this.maxUniqueSelection !== undefined
                ? { maxUniqueSelection: this.maxUniqueSelection }
                : {})
        };
    }

    static fromJSON(data: Record<string, unknown>): CartModifierGroup {
        if (typeof data.modifierGroupId !== 'number') {
            throw new Error("CartModifierGroup.fromJSON: modifierGroupId must be a number.");
        }
        if (typeof data.name !== 'string') {
            throw new Error("CartModifierGroup.fromJSON: name must be a string.");
        }

        const modifiers = Array.isArray(data.modifiers)
            ? data.modifiers.map((mod: any) => CartModifier.fromJSON(mod))
            : [];

        const num = (v: unknown): number | undefined =>
            typeof v === 'number' ? v : undefined;
        const bool = (v: unknown): boolean | undefined =>
            typeof v === 'boolean' ? v : undefined;

        return new CartModifierGroup({
            modifierGroupId: data.modifierGroupId,
            name: data.name,
            modifiers,
            selectionRequirement: data.selectionRequirement as 'Optional' | 'Required' | undefined,
            minSelection: num(data.minSelection),
            maxSelection: num(data.maxSelection),
            allowMultipleSelection: bool(data.allowMultipleSelection),
            allowDuplicateSelection: bool(data.allowDuplicateSelection),
            maxDuplicatesPerModifier: num(data.maxDuplicatesPerModifier),
            minUniqueSelection: num(data.minUniqueSelection),
            maxUniqueSelection: num(data.maxUniqueSelection)
        });
    }
}
